export type FlowClassification = 'SupplyAir' | 'ReturnAir' | 'HydronicSupply' | 'HydronicReturn' | 'Sanitary' | 'DomesticColdWater';

export interface MEPNode {
    id: string; // Element ID
    type: 'Source' | 'Terminal' | 'Junction';
    flow: number; // For Terminals: demand/supply. For Junctions: accumulated.
}

export interface MEPLink {
    id: string; // Segment ID (Duct/Pipe)
    sourceNodeId: string;
    targetNodeId: string;
    flow: number; // Flow traveling through this link (L/s or CFM)

    // Physics Config
    size: number; // Hydraulic Diameter (meters)
    length: number; // meters
    roughness: number; // meters (Default 0.00015 for galvanized steel)

    // Calculated
    velocity: number; // m/s
    frictionLoss: number; // Pascals (Pa)
}

export class MEPSystemLogic {
    private nodes: Map<string, MEPNode> = new Map();
    private links: Map<string, MEPLink> = new Map();
    // Adjacency List: NodeID -> LinkID[]
    private adjacency: Map<string, string[]> = new Map();

    constructor(public systemClassification: FlowClassification) { }

    addNode(id: string, type: MEPNode['type'], flow: number = 0): void {
        this.nodes.set(id, { id, type, flow });
        if (!this.adjacency.has(id)) this.adjacency.set(id, []);
    }

    addLink(id: string, startNodeId: string, endNodeId: string, size: number, length: number, roughness: number = 0.00015): void {
        this.links.set(id, {
            id,
            sourceNodeId: startNodeId,
            targetNodeId: endNodeId,
            flow: 0,

            size,
            length,
            roughness,

            velocity: 0,
            frictionLoss: 0
        });

        // Add to adjacency (undirected for graph structure, flow determines direction)
        this.adjacency.get(startNodeId)?.push(id);
        this.adjacency.get(endNodeId)?.push(id);
    }

    /**
     * Propagate flow from "Terminals" back to "Source" (Consumer-Producer model).
     * Calculates Flow, Velocity, and Pressure Drop.
     */
    calculateFlows(rootNodeId: string): void {
        // Reset flows
        this.links.forEach(l => {
            l.flow = 0;
            l.velocity = 0;
            l.frictionLoss = 0;
        });

        const visited = new Set<string>();
        this.sumFlowRecursive(rootNodeId, visited);
    }

    private sumFlowRecursive(nodeId: string, visited: Set<string>): number {
        visited.add(nodeId);
        const node = this.nodes.get(nodeId);
        if (!node) return 0;

        let totalFlow = node.flow; // Terminal flow (non-zero for terminals)

        const connectedLinks = this.adjacency.get(nodeId) || [];

        for (const linkId of connectedLinks) {
            const link = this.links.get(linkId);
            if (!link) continue;

            // Find other end
            const neighborId = link.sourceNodeId === nodeId ? link.targetNodeId : link.sourceNodeId;

            if (!visited.has(neighborId)) {
                // Recursive step: Get flow from downstream branch
                const branchFlow = this.sumFlowRecursive(neighborId, visited);

                // Assign flow to this link
                link.flow = branchFlow;
                totalFlow += branchFlow;

                // Calculate Physics immediately
                this.calculateLinkPhysics(link);
            }
        }

        return totalFlow;
    }

    private calculateLinkPhysics(link: MEPLink): void {
        // 1. Velocity (v = Q / A)
        // flow is usually L/s or CFM. Let's assume m3/s for calc, but storage might be different.
        // Let's standard: Storage = m3/s ? Or L/s?
        // Let's assume Storage = L/s.

        const bfs_flow = link.flow / 1000; // convert L/s to m3/s
        if (bfs_flow <= 0 || link.size <= 0) {
            link.velocity = 0;
            link.frictionLoss = 0;
            return;
        }

        const area = Math.PI * Math.pow(link.size / 2, 2);
        link.velocity = bfs_flow / area;

        // 2. Pressure Drop (Darcy-Weisbach)
        // dP = f * (L/D) * (rho * v^2 / 2)

        const f = this.calculateFrictionFactor(link.roughness, link.size, link.velocity);
        const rho = 1.225; // Air density kg/m3 (approx) - Should vary by system type

        link.frictionLoss = f * (link.length / link.size) * (rho * Math.pow(link.velocity, 2) / 2);
    }

    private calculateFrictionFactor(e: number, D: number, v: number): number {
        // Colebrook-White approximation or Swamee-Jain
        // Re = (rho * v * D) / mu
        // mu_air = 1.81e-5 Pa.s
        if (v === 0) return 0;

        const rho = 1.225;
        const mu = 1.81e-5;
        const Re = (rho * v * D) / mu;

        if (Re < 2300) return 64 / Re; // Laminar

        // Swamee-Jain (Turbulent)
        return 0.25 / Math.pow(Math.log10((e / (3.7 * D)) + (5.74 / Math.pow(Re, 0.9))), 2);
    }

    getLinkFlow(linkId: string): number {
        return this.links.get(linkId)?.flow || 0;
    }

    getLinkPressureDrop(linkId: string): number {
        return this.links.get(linkId)?.frictionLoss || 0;
    }
}
